import { Field, useField } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import {
  SZ,
  Section,
  Grid,
  Field2,
  RffDate,
  RffMoney,
  RffNum,
  RffStepper,
  RffSwitch,
  RepeatItem,
  AddButton,
  SizerMuiField,
} from "../../sizersShared/sizerUi";
import RffAddressEditingFieldGoogle from "../../../components/finalForm/inputs/RffAddressEditingFieldGoogle";
import { selectOptionsPropertyType } from "../../sizersShared/sizerSelectOptions";
import RffSelectField from "../../../components/finalForm/inputs/RffSelectField";
import { isPurchaseLike } from "../../../utils/loans/delayedPurchase";
import {
  ClosingCostsFields,
  InterestReserveFields,
} from "../../sizersShared/FinancedCostsFields";

// `address` is left EMPTY, not pre-shaped: the Places picker writes the whole
// Address object in one go (see the address field below), and seeding partial
// keys only makes a half-filled address look real.
// Exported so the screen can seed the first (blank) property row and build its
// dev-only demo scenario from the same shape.
export const newProperty = () => ({
  address: {},
  asIsValue: 0,
  purchasePrice: 0,
  // NO DEFAULT. Property type is a required answer and it decides which of the
  // project fields appear — a silent "SFR" answers a question nobody was asked.
  // `propertyConditionAtOrigination` / `propertyStatusAtOrigination` moved to loan
  // creation, so neither is seeded here either.
  propertyType: "",
  constructionBudget: 0,
  additionalBasis: 0,
  arv: 0,
  residentialSqFootage: 0,
  // `numberOfUnits`, NOT `unitCount`: on SubjectProperty `unitCount` is a
  // COMPUTED property derived from `units[]` and cannot be set — posting it
  // writes a value the model will never read back.
  numberOfUnits: 1,
  propertyRuralIndicator: false,
  floodZone: false,
});

// The two products that carry a construction project.
const PROJECT_PRODUCTS = ["Fix and Flip", "Ground Up Construction"];

/**
 * The address control, cut down to the height of a plain input.
 *
 * `RffAddressEditingFieldGoogle` is padded for a sidebar — a 12px/14px text cell
 * and two `size="small"` icon buttons put its filled state at ~46px, which reads
 * as a mismatch next to the 38px select sitting beside it. These overrides trim
 * the cell's vertical padding and shrink the icons to fit the same 38px.
 *
 * STRUCTURAL SELECTORS, because the control takes no `sx` and its display row
 * carries no class of its own: `.input-wrapper-root` is `InputWrapper`'s root,
 * its `> div` is the bordered display box, and the fourth level down is the
 * address text cell (the icon column at the same level holds buttons, not divs,
 * so it is not caught). Only the FILLED state is padded this way — the
 * autocomplete and the expanded edit panel are MUI fields that `SizerMuiField`
 * already sizes.
 */
/**
 * The height BOTH controls on the address row are pinned to.
 *
 * They are different beasts — a bordered display box the Places control renders
 * itself, and a MUI select — so neither picks up the other's sizing on its own.
 * One constant, applied to both, is what keeps them level; changing the row's
 * height means changing this and nothing else.
 */
const FIELD_H = 42;

/**
 * Property type. It sits outside `SizerMuiField` by default, which is exactly
 * why it drifted: unwrapped, it takes the APP theme's small-select metrics
 * rather than the sizer's, so it rendered a couple of pixels taller than the
 * address beside it, with MUI's border colour and radius to match. Wrapping it
 * puts it on the same normalization as every other MUI control in the sizers,
 * and the explicit height holds it exactly level with the address box.
 */
const TYPE_SELECT_SX = {
  "& .MuiOutlinedInput-root": { height: FIELD_H, minHeight: FIELD_H },
};

const ADDRESS_ROW_SX = {
  /* The label. This control brings its own `InputWrapper` rather than sitting
     under a `FieldLabel`, so it renders through MUI's `InputLabel` and inherits
     the theme's resting-label grey — noticeably lighter and a shade smaller than
     every other label on the card. Restate FieldLabel's type here so the row
     reads as one form rather than as a component borrowed from elsewhere. */
  "& .input-wrapper-root .MuiInputLabel-root": {
    height: 22,
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 500,
    color: SZ.ink500,
    marginBottom: "6px",
  },
  "& .input-wrapper-root > div": {
    borderColor: SZ.lineStrong,
    borderRadius: SZ.radiusSm,
    minHeight: FIELD_H,
  },
  "& .input-wrapper-root > div > div > div > div": {
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  "& .input-wrapper-root .MuiIconButton-root": { padding: "4px" },
  "& .input-wrapper-root .MuiIconButton-root .MuiSvgIcon-root": { fontSize: 17 },
};

const TypeSub = ({ name }) => (
  <Field name={name} subscription={{ value: true }}>
    {({ input }) => (input.value ? String(input.value) : "Type not set")}
  </Field>
);

export default function PropertiesSection({ first, result }) {
  /* Purchase price is asked only on a PURCHASE. On a refi there is no purchase
     price to give — the cost basis is the as-is value plus `additionalBasis`,
     which is exactly how the screen's own estimate reads it (`estimateAmounts`
     in SizerScreen.js) and how the engine derives the LTC basis server-side.
     Leaving the field on screen invites a number that prices nothing.

     Same `isPurchaseLike` rule the estimate uses, so the two can never
     disagree — a delayed purchase (cash-out + the flag, or a legacy "Delayed
     Purchase" value) counts as a purchase, which does have one.

     Hiding it does NOT clear it (react-final-form keeps values for unregistered
     fields), so a purpose flipped purchase → refi still carries whatever was
     typed. That is deliberate — flipping back restores the number, and the
     engine reads the refi basis off as-is + additionalBasis, never off
     purchasePrice.

     A single `useField` subscription rather than `useFormState`: this section
     re-renders every property card, and watching the whole form would do that
     on every keystroke anywhere in the scenario. */
  const { input: loanPurpose } = useField("loanPurpose", {
    subscription: { value: true },
  });
  const { input: delayedFlag } = useField("delayedPurchase", {
    subscription: { value: true },
  });
  const isPurchase = isPurchaseLike(loanPurpose.value, delayedFlag.value);

  /* Construction budget, ARV and the project-scope questions exist only where
     there IS a project. A stabilized bridge has nothing to renovate, so asking
     for a budget or an after-repair value invites numbers that price nothing. */
  const { input: product } = useField("loanProductType", {
    subscription: { value: true },
  });
  const isProject = PROJECT_PRODUCTS.includes(product.value);

  return (
    <Section
      first={first}
      title="Subject properties"
      subtitle="Add every property this loan will cover."
    >
      <FieldArray name="subjectProperties">
        {({ fields }) => (
          <>
            {fields.map((f, index) => (
              <RepeatItem
                key={f}
                title={`Property ${index + 1}`}
                sub={<TypeSub name={`${f}.propertyType`} />}
                onRemove={() => fields.remove(index)}
                removeLabel={`Remove property ${index + 1}`}
                defaultCollapsed={index > 0}
              >
                {/* THE standard property-address control — the same component,
                    with the same props, that the drilldown's Add Property
                    sidebar and the property edit form use. Google Places writes
                    the whole Address OBJECT onto `${f}.address` in one go
                    (fullAddress / streetNumber / streetName / unit / city /
                    state / zip / county / country / lat / lng), which is the
                    shape pipeline.py `Address` expects.

                    It replaced five hand-rolled text inputs that only ever set
                    streetName / addressUnitNumber / city / state / zip — no
                    streetNumber, no county, no coordinates and no `fullAddress`,
                    so the saved deal carried a thinner address than one created
                    anywhere else in the portal. The canonical field the pricing
                    engine reads off this object is `zip` (`_loan_zip`), which is
                    also what the market-data lookup keys on.

                    `allowManualEntry` keeps the raw-land / new-subdivision
                    escape hatch (ZIP-centroid geocode + `manualEntry: true`) —
                    exactly as Add Property enables it. It is purely client-side
                    (Places REST), so it needs no dealId and works before the
                    scenario is ever saved. */}
                {/* Address takes 8 of 12 — it is the longest value on the card
                    and the one people scan for — with property type beside it
                    rather than orphaned on its own row below. */}
                <Grid cols={12}>
                  <Field2 span={8}>
                    <SizerMuiField sx={ADDRESS_ROW_SX}>
                      <RffAddressEditingFieldGoogle
                        name={`${f}.address`}
                        label="Property address"
                        allowManualEntry
                      />
                    </SizerMuiField>
                  </Field2>
                  {/* No default property type — it is a required answer, not a
                      guess. Construction budget, ARV and the project fields all
                      key off it, and a silent "SFR" makes the wrong set of them
                      appear. */}
                  <Field2 label="Property type" required span={4}>
                    <SizerMuiField sx={TYPE_SELECT_SX}>
                      <RffSelectField
                        name={`${f}.propertyType`}
                        options={selectOptionsPropertyType}
                        required
                        sm
                      />
                    </SizerMuiField>
                  </Field2>
                </Grid>

                <Grid cols={2} sx={{ mt: "24px" }}>
                  {/* PURCHASE: the price and the date are required — the
                      canonical basis is MIN(as-is, purchase), so a missing
                      purchase price silently prices off as-is alone.
                      REFINANCE: the SAME two fields, asked as the ORIGINAL
                      purchase and optional (`purchaseDate` carries the alias
                      `originalPurchaseDate` on the model for exactly this). */}
                  <Field2
                    label={isPurchase ? "Purchase price" : "Original purchase price"}
                    required={isPurchase}
                  >
                    <RffMoney
                      name={`${f}.purchasePrice`}
                      placeholder="0"
                      required={isPurchase}
                    />
                  </Field2>
                  <Field2
                    label={isPurchase ? "Purchase date" : "Original purchase date"}
                    required={isPurchase}
                  >
                    <RffDate name={`${f}.purchaseDate`} required={isPurchase} />
                  </Field2>
                  {/* A construction budget only exists on the two products that
                      carry one; on a stabilized bridge there is nothing to
                      renovate and the field would price nothing. */}
                  {isProject && (
                    <Field2
                      label="Construction budget"
                      hint="Hard and soft costs."
                    >
                      <RffMoney
                        name={`${f}.constructionBudget`}
                        placeholder="0"
                      />
                    </Field2>
                  )}
                  {/* LTC is a cost-basis metric, and on a NON-PURCHASE scenario
                      there is no purchase price to be the basis — carried rehab
                      spend and other capitalized cost is. Meaningless on a
                      purchase, where the price IS the basis. */}
                  {!isPurchase && (
                    <Field2
                      label="Additional basis"
                      hint="Refi / rehab carry — adds to the LTC cost basis."
                    >
                      <RffMoney name={`${f}.additionalBasis`} placeholder="0" />
                    </Field2>
                  )}
                  <Field2 label="As-is value">
                    <RffMoney name={`${f}.asIsValue`} placeholder="0" />
                  </Field2>
                  {/* ARV is the exit value of a project — there is no "after
                      repair" on a product with no repairs. */}
                  {isProject && (
                    <Field2 label="After-repair value">
                      <RffMoney name={`${f}.arv`} placeholder="0" />
                    </Field2>
                  )}
                </Grid>
                <Grid cols={4} sx={{ mt: "24px" }}>
                  <Field2 label="Units">
                    <RffStepper name={`${f}.numberOfUnits`} placeholder="1" min={1} />
                  </Field2>
                  <Field2 label="Sq. footage">
                    <RffNum
                      name={`${f}.residentialSqFootage`}
                      placeholder="0"
                    />
                  </Field2>
                  <Field2 label="Flood zone">
                    <RffSwitch name={`${f}.floodZone`} />
                  </Field2>
                  <Field2 label="Rural">
                    <RffSwitch name={`${f}.propertyRuralIndicator`} />
                  </Field2>
                </Grid>

                {/* PROJECT SCOPE — asked only where there is a project. Both
                    feed the property model's own feasibility test: a >20%
                    expansion or a change of use is one of the four triggers for
                    a third-party feasibility report.

                    Square footage is asked as the COMPLETED total rather than a
                    "will it grow?" toggle, because that is the shape of the
                    field behind it (`afterRenoSqFootage`) — the model computes
                    the change from it against today's footage. */}
                {isProject && (
                  <Grid cols={2} sx={{ mt: "24px" }}>
                    <Field2
                      label="Sq. footage after the project"
                      hint="Leave blank if the project does not change the footprint."
                    >
                      <RffNum
                        name={`${f}.afterRenoSqFootage`}
                        placeholder="0"
                      />
                    </Field2>
                    <Field2 label="Changes primary use or zoning">
                      <RffSwitch name={`${f}.changeOfUseFlag`} />
                    </Field2>
                  </Grid>
                )}

                {/* Closing costs and the interest reserve, each financed or
                    borrower cash (pricing-scenario-sizers.md §12b). The reserve
                    answer is always owed on the two project products. */}
                <ClosingCostsFields
                  name={f}
                  index={index}
                  result={result}
                  isPurchase={isPurchase}
                />
                <InterestReserveFields
                  name={f}
                  index={index}
                  result={result}
                  askFunding={isProject}
                />
              </RepeatItem>
            ))}

            <AddButton onClick={() => fields.push(newProperty())}>
              Add property
            </AddButton>
          </>
        )}
      </FieldArray>
    </Section>
  );
}
